import * as React from "react"
import {
  Button,
  Modal,
  ModalCard,
  StyledModal,
  StyledModalActions,
  StyledModalBody,
  StyledModalHeader,
  Text,
  useShowSuccessToast,
} from "gatsby-interface"
import {
  ui as uiText,
  logDrainIntegration as text,
} from "@modules/locales/default.js"
import { LogDrainIntegration } from "@modules/graphql/types"
import {
  commonMessageTags,
  FormattedMessage,
  interpolateMessage,
} from "@modules/locales"
import { StyledForm } from "@modules/ui/components/Primitives"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { logDrainVendorNames } from "../../shared/constants"
import { useDeleteLogDrainIntegrationMutation } from "../../shared/queries.generated"

export type LogDrainDeleteModalProps = {
  siteName: string
  siteId?: string
  onClose: () => void
  logDrain: LogDrainIntegration
  onSuccess: () => void
}

export function LogDrainDeleteModal({
  siteName,
  siteId,
  logDrain,
  onClose,
  onSuccess,
}: LogDrainDeleteModalProps) {
  const logDrainProvider = logDrain.logDrainProvider
  const vendorName = logDrainVendorNames[logDrainProvider]

  const [deleteMutation] = useDeleteLogDrainIntegrationMutation()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()

  return (
    <Modal
      isOpen={Boolean(logDrain)}
      aria-label={text.headers.deleteIntegration}
      onDismiss={onClose}
      type="error"
    >
      <ModalCard>
        <StyledModal variant="WARNING">
          <StyledModalHeader onCloseButtonClick={onClose}>
            {text.headers.deleteIntegration}
          </StyledModalHeader>
          <StyledModalBody>
            <Text>
              <FormattedMessage<"logDrainProvider" | "siteName", "strong">
                message={text.messages.areYouSure}
                values={{
                  logDrainProvider,
                  siteName,
                }}
                tags={commonMessageTags}
              />
            </Text>
            <StyledForm
              onSubmit={e => {
                e.preventDefault()
                deleteMutation({
                  variables: {
                    siteId,
                    integrationId: logDrain.id,
                  },
                })
                  .then(() => {
                    onClose()
                    onSuccess()
                    showSuccessToast(
                      interpolateMessage<"logDrainProvider">(
                        text.messages.logDrainIntegrationRemoved,
                        {
                          logDrainProvider,
                        }
                      )
                    )
                  })
                  .catch((err: Error) => setError(err))
              }}
            >
              {errorAlert}
              <StyledModalActions>
                <Button
                  variant="SECONDARY"
                  tone="NEUTRAL"
                  type="button"
                  onClick={onClose}
                >
                  {uiText.actions.cancel}
                </Button>
                <Button variant="PRIMARY" tone="DANGER" type="submit">
                  {text.actions.delete}
                </Button>
              </StyledModalActions>
            </StyledForm>
          </StyledModalBody>
        </StyledModal>
      </ModalCard>
    </Modal>
  )
}
